/* eslint-disable react/no-multi-comp -- panel is wrapped in ProductProvider for breadcrumbs */
import React, {useEffect, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Col} from 'react-bootstrap';
import {ProductProvider} from 'contexts/ProductContext';
import {Header} from 'components/customer/RoomPlanner/components/Menu/Header';
import {PlaceholderItems} from 'components/customer/RoomPlanner/components/Menu/PlaceholderItems';
import PlaceholderEditor from 'components/customer/RoomPlanner/components/Menu/PlaceholderEditor';
import {TitleAndBreadcrumbs} from 'components/customer/Product/TitleAndBreadcrums';
import {Search} from 'shared/components/Product/Search';
import {MobileControls} from 'components/customer/Product/ProductsWithProvider';
import {CancelButton} from 'shared/components/CancelButton';
import {useAppSelector} from 'store/customer';
import {selectSearchText} from 'components/customer/Product/store/productSlice';
import {getisRoomPlannerEditing} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';
import {useGetPlaceholderFacesQuery} from 'components/customer/RoomPlanner/store/placeholderFaceApi';
import {
    filterPlaceholderCatalogItems,
    getPlaceablePlaceholderItems,
    getPlaceholderCatalogSubCategory,
    getPlaceholderCategoriesWithItems,
} from 'components/customer/RoomPlanner/helpers/placeholder/catalog';
import {CategoryInterface} from 'shared/components/Product/Category';
import {
    PLACEHOLDER_EDIT_PARAM,
    PLACEHOLDER_FACE_PARAM,
} from 'components/customer/RoomPlanner/helpers/placeholder/editorParams';

import 'components/customer/Product/product.scss';

/**
 * Room planner appliance catalog — separate from Add Product.
 * @return {JSX.Element}
 */
export const ApplianceSelector = () => {
    return (
        <ProductProvider miniBrowser={true}>
            <AppliancePanel />
        </ProductProvider>
    );
};

const AppliancePanel = () => {
    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();
    const isMobileDevice = isMobileLandscape || isPortraitDevices;
    const [searchParams, setSearchParams] = useSearchParams();
    const {data: placeholderFaces = []} = useGetPlaceholderFacesQuery();
    const search = useAppSelector(selectSearchText);
    const isRoomPlannerEditing = useAppSelector(getisRoomPlannerEditing);

    // One editor instance per placeholder/catalog face: remounting on switch
    // lets the editor's unmount cleanup discard the preview it was showing.
    const editorKey =
        searchParams.get(PLACEHOLDER_EDIT_PARAM) ||
        searchParams.get(PLACEHOLDER_FACE_PARAM);
    const isEditor = !!editorKey;

    useEffect(() => {
        /*
         * A cabinet product edit is taking over the sidebar (the user
         * clicked a cabinet while this panel was open). This panel is
         * still mounted for one render, and pulling the URL back onto the
         * appliance catalog here would wipe the cabinet editor params
         * before the menu can switch.
         */
        if (searchParams.get('cabinetId')) {
            return;
        }

        /*
         * An editor session is opening. Router navigations are deferred
         * (React transitions) while Redux updates land immediately, so on
         * the first render after Edit is clicked this panel can mount with
         * the pre-Edit URL. Navigating from that stale URL here would
         * overwrite the editor params (`placeholderEdit`, `preventReset`)
         * that are still on their way in — that made Edit need two clicks.
         */
        if (isRoomPlannerEditing || isEditor) {
            return;
        }

        const leftoverCabinetSubCategory =
            searchParams.has('subCategory') &&
            !getPlaceholderCatalogSubCategory(searchParams.get('subCategory'));

        if (searchParams.get('category') === 'placeholder') {
            if (!leftoverCabinetSubCategory) {
                return;
            }
            const next = new URLSearchParams(searchParams);
            next.delete('subCategory');
            setSearchParams(next, {replace: true});
            return;
        }

        const next = new URLSearchParams(searchParams);
        next.set('category', 'placeholder');
        next.delete('product');
        next.delete('cabinetId');
        next.delete('subCategory');
        next.delete('favourites');
        setSearchParams(next, {replace: true});
    }, [searchParams, setSearchParams, isRoomPlannerEditing, isEditor]);

    const placeholderItems = useMemo(
        () => getPlaceablePlaceholderItems(placeholderFaces),
        [placeholderFaces]
    );
    const placeholderSubCategory = getPlaceholderCatalogSubCategory(
        searchParams.get('subCategory')
    );

    const headerCategories = useMemo((): CategoryInterface[] => {
        if (
            placeholderSubCategory ||
            (typeof search === 'string' && search.trim().length > 0)
        ) {
            return [];
        }

        return getPlaceholderCategoriesWithItems(placeholderFaces).map(
            (name) => ({
                id: 0,
                name,
                imageUrl: '',
                action: () => undefined,
                productCount: placeholderItems.filter(
                    ({face}) => face.category === name
                ).length,
            })
        );
    }, [placeholderFaces, placeholderItems, placeholderSubCategory, search]);

    const visibleItems = useMemo(() => {
        const source = placeholderSubCategory
            ? placeholderItems.filter(
                  ({face}) => face.category === placeholderSubCategory
              )
            : placeholderItems;

        return filterPlaceholderCatalogItems(source, search ?? '');
    }, [placeholderItems, placeholderSubCategory, search]);

    const itemsCount = visibleItems.length;

    return (
        <div
            className={
                isEditor ? 'Products product bottomMargin' : 'Products product'
            }>
            <div className="product-content">
                <Header />
                <div>
                    {isEditor ? null : (
                        <Search
                            isProductPage={false}
                            placeholder='Try searching "oven", "fridge", or "dishwasher"...'
                        />
                    )}
                    <div className="productsAndCategories">
                        {isEditor ? (
                            <PlaceholderEditor key={editorKey} />
                        ) : (
                            <>
                                <TitleAndBreadcrumbs
                                    categories={headerCategories}
                                    itemsCount={itemsCount}
                                />
                                <PlaceholderItems />
                            </>
                        )}
                    </div>
                    {isMobileDevice && !isEditor ? (
                        <MobileControls className="control-buttons">
                            <Col xs={{offset: 3, span: 6}}>
                                <CancelButton
                                    noDialog={true}
                                    noRedirect={true}
                                />
                            </Col>
                        </MobileControls>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

/* eslint-enable react/no-multi-comp -- panel is wrapped in ProductProvider for breadcrumbs */
